import React, { useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Layout } from '../components/layout';
import { Button, Input, Card, CardBody } from '../components/common';
import { OAuthButtons, OAuthDivider } from '../components/auth';
import { useAuth } from '../hooks/useAuth';

export const Register = () => {
  const navigate = useNavigate();
  const { register: registerUser, isAuthenticated } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const password = watch('password');

  // Redirect already authenticated users
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data) => {
    setError('');
    setLoading(true);

    try {
      await registerUser(data.email, data.password, data.displayName);
      navigate('/dashboard');
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.details) {
        setError(errorData.details.map(d => d.message).join(', '));
      } else {
        setError(errorData?.error || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          <CardBody>
            <h2 className="text-2xl font-bold text-center mb-6">Create Your Account</h2>

            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            <OAuthButtons action="sign up" />

            <OAuthDivider />

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Input
                label="Display Name"
                {...register('displayName', {
                  required: 'Display name is required',
                  minLength: {
                    value: 2,
                    message: 'Display name must be at least 2 characters',
                  },
                  maxLength: {
                    value: 30,
                    message: 'Display name cannot exceed 30 characters',
                  },
                })}
                error={errors.displayName?.message}
                placeholder="How others will see you"
              />

              <Input
                label="Email"
                type="email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={errors.email?.message}
                placeholder="you@example.com"
              />

              <div>
                <Input
                  label="Password"
                  type="password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                    validate: {
                      hasLowercase: (v) =>
                        /[a-z]/.test(v) || 'Must contain a lowercase letter',
                      hasUppercase: (v) =>
                        /[A-Z]/.test(v) || 'Must contain an uppercase letter',
                      hasNumber: (v) =>
                        /[0-9]/.test(v) || 'Must contain a number',
                    },
                  })}
                  error={errors.password?.message}
                  placeholder="Min 8 chars, mixed case + number"
                />
              </div>

              <Input
                label="Confirm Password"
                type="password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (v) => v === password || 'Passwords do not match',
                })}
                error={errors.confirmPassword?.message}
                placeholder="Re-enter your password"
              />

              <Button
                type="submit"
                className="w-full"
                loading={loading}
              >
                Create Account
              </Button>
            </form>

            <p className="mt-6 text-center text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-600 hover:underline">
                Log in
              </Link>
            </p>
          </CardBody>
        </Card>
      </div>
    </Layout>
  );
};
